import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";

import { apiFetch, ApiError } from "@/lib/api";
import { formatEcnNumber, formatEcoNumber, formatTimestamp } from "@/lib/documents";
import { SupplierSearchableSelect } from "@/components/mrp/SupplierSearchableSelect";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { Input } from "@/components/ui/Input";
import { HoverHintTrigger, PageTitleWithHelp, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import {
  changeNoticeHtmlPreviewUrl,
  downloadChangeNoticePdf,
  fetchChangeOrderDetail,
  uploadChangeNoticeCmResponse,
  type ChangeOrderDetail,
  type EngineeringChangeNotice,
} from "@/pages/documents/engineeringChangeOrderApi";
import { EngineeringChangeMarkdown } from "@/pages/documents/EngineeringChangeMarkdown";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";

function noticeStatusClass(status: EngineeringChangeNotice["status"]): string {
  if (status === "verified") return "bg-green-50 text-green-700";
  if (status === "implemented") return "bg-cyan-50 text-cyan-700";
  if (status === "acknowledged") return "bg-blue-50 text-blue-700";
  if (status === "issued") return "bg-amber-50 text-amber-800";
  if (status === "cancelled") return "bg-red-50 text-red-700";
  return "bg-slate-100 text-slate-700";
}

export function DocumentChangeOrderDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const qualityContext = location.pathname.startsWith("/quality/");
  const listPath = qualityContext ? "/quality/engineering-changes" : "/documents/change-orders";
  const documentsWrite = useHubModuleWrite("documents");
  const qualityWrite = useHubModuleWrite("quality");
  const canWriteEco = documentsWrite;
  const canWriteEcn = documentsWrite || qualityWrite;
  const readOnlyEcoHint = "Your role has read-only access to Document Control.";
  const readOnlyEcnHint = "Your role has read-only access to Quality and Document Control.";

  const queryClient = useQueryClient();
  const detailQuery = useQuery<ChangeOrderDetail, ApiError>({
    queryKey: ["document-change-order-detail", id],
    queryFn: () => fetchChangeOrderDetail(id!),
    enabled: Boolean(id),
  });

  const collectionsQuery = useQuery({
    queryKey: ["document-collections"],
    queryFn: () =>
      apiFetch<{ collections: { id: string; code: string; name: string }[] }>("/api/document-collections"),
  });

  const detail = detailQuery.data;
  const eco = detail?.changeOrder;

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [implementationPlan, setImplementationPlan] = useState("");
  const [changeReason, setChangeReason] = useState("");
  const [enforcementRule, setEnforcementRule] = useState("");
  const [notes, setNotes] = useState("");
  const [collectionIds, setCollectionIds] = useState<string[]>([]);
  const [newNoticeSupplierId, setNewNoticeSupplierId] = useState<number | null>(null);
  const [newNoticeSupplierSearch, setNewNoticeSupplierSearch] = useState("");
  const cmResponseInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  useEffect(() => {
    if (!detail?.changeOrder) return;
    setTitle(detail.changeOrder.title);
    setDescription(detail.changeOrder.description ?? "");
    setImplementationPlan(detail.changeOrder.implementation_plan ?? "");
    setChangeReason(detail.changeOrder.change_reason ?? "");
    setEnforcementRule(detail.changeOrder.enforcement_rule ?? "");
    setNotes(detail.changeOrder.notes ?? "");
    setCollectionIds(detail.productCollections.map((row) => row.product_collection_id));
  }, [detail?.changeOrder?.id]);

  const dirty =
    eco != null &&
    (title !== eco.title ||
      description !== (eco.description ?? "") ||
      implementationPlan !== (eco.implementation_plan ?? "") ||
      changeReason !== (eco.change_reason ?? "") ||
      enforcementRule !== (eco.enforcement_rule ?? "") ||
      notes !== (eco.notes ?? "") ||
      collectionIds.join(",") !== detail!.productCollections.map((row) => row.product_collection_id).join(","));

  const saveMutation = useMutation({
    mutationFn: () =>
      apiFetch<ChangeOrderDetail>(`/api/document-change-orders/${id}`, {
        method: "PATCH",
        body: {
          title: title.trim(),
          description: description.trim() || null,
          implementationPlan: implementationPlan.trim() || null,
          changeReason: changeReason.trim() || null,
          enforcementRule: enforcementRule.trim() || null,
          notes: notes.trim() || null,
          productCollectionIds: collectionIds,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-change-order-detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["document-change-orders"] });
    },
  });

  const ecoActionMutation = useMutation({
    mutationFn: (action: "submit" | "approve" | "implement" | "cancel") =>
      apiFetch<{ ok: true }>(`/api/document-change-orders/${id}/${action}`, { method: "POST" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-change-order-detail", id] });
      void queryClient.invalidateQueries({ queryKey: ["document-change-orders"] });
    },
  });

  const createNoticeMutation = useMutation({
    mutationFn: () =>
      apiFetch(`/api/document-change-orders/${id}/change-notices`, {
        method: "POST",
        body: { supplierDataId: newNoticeSupplierId },
      }),
    onSuccess: () => {
      setNewNoticeSupplierId(null);
      void queryClient.invalidateQueries({ queryKey: ["document-change-order-detail", id] });
    },
  });

  const noticeActionMutation = useMutation({
    mutationFn: (input: { noticeId: string; action: string; body?: Record<string, unknown> }) =>
      apiFetch(`/api/document-change-orders/${id}/change-notices/${input.noticeId}/${input.action}`, {
        method: "POST",
        body: input.body,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-change-order-detail", id] });
    },
  });

  const patchNoticeMutation = useMutation({
    mutationFn: (input: { noticeId: string; verificationResult?: string }) =>
      apiFetch(`/api/document-change-orders/${id}/change-notices/${input.noticeId}`, {
        method: "PATCH",
        body: {
          verificationResult: input.verificationResult,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-change-order-detail", id] });
    },
  });

  const ecnPdfMutation = useMutation({
    mutationFn: (noticeId: string) => downloadChangeNoticePdf(id!, noticeId),
  });

  const ecnCmResponseMutation = useMutation({
    mutationFn: (input: { noticeId: string; file: File }) =>
      uploadChangeNoticeCmResponse(id!, input.noticeId, input.file),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document-change-order-detail", id] });
    },
  });

  if (!id) {
    return <p className="text-sm text-red-600">Missing change order id.</p>;
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          breadcrumbLabel={eco ? formatEcoNumber(eco.number) : "Engineering change"}
          hoverHint="Engineering change order (ECO) authorizes the change. Engineering change notices (ECNs) track each contract manufacturer recipient. No email is sent from the hub."
        >
          {eco ? formatEcoNumber(eco.number) : "Engineering change"}
        </PageTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          {eco ? (
            <span className="rounded-full bg-slate-100 px-2 py-1 text-sm font-medium text-slate-800">
              {eco.status}
            </span>
          ) : null}
          {eco?.status === "approved" ? (
            <ReadOnlyHoverLayer active={!canWriteEco} hoverHint={readOnlyEcoHint}>
              <HoverHintTrigger
                trigger={
                  <Button
                    type="button"
                    size="sm"
                    variant="primary"
                    disabled={!canWriteEco}
                    onClick={() => ecoActionMutation.mutate("implement")}
                  >
                    Mark ECO implemented
                  </Button>
                }
              >
                Marks the internal order complete. Any linked draft document revisions become current and superseded revisions become obsolete. Contract manufacturer notices continue separately.
              </HoverHintTrigger>
            </ReadOnlyHoverLayer>
          ) : null}
          <Link to={listPath}>
            <Button type="button" size="sm" variant="ghost">Back to list</Button>
          </Link>
          {eco?.status === "draft" ? (
            <ReadOnlyHoverLayer active={!canWriteEco} hoverHint={readOnlyEcoHint}>
              <HubSaveButton dirty={dirty} disabled={!canWriteEco} onClick={() => saveMutation.mutate()} />
            </ReadOnlyHoverLayer>
          ) : null}
        </div>
      </header>

      {detailQuery.isError ? (
        <p className="text-sm text-red-600">{detailQuery.error.message}</p>
      ) : detailQuery.isLoading || !eco ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : (
        <>
          <Card>
            <CardHeader className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle>{eco.title}</CardTitle>
              <div className="text-sm">
                <span className="text-slate-600">
                  {eco.document_count} linked {eco.document_count === "1" ? "document" : "documents"}
                </span>
              </div>
            </CardHeader>
            <CardBody className="space-y-3">
              {eco.status === "draft" ? (
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="block text-sm font-medium text-slate-700 md:col-span-2">
                    Title
                    <Input className="mt-1" value={title} onChange={(event) => setTitle(event.target.value)} disabled={!canWriteEco} />
                  </label>
                  <label className="block text-sm font-medium text-slate-700 md:col-span-2">
                    Description (Markdown)
                    <textarea className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 font-mono text-sm" rows={18} value={description} onChange={(event) => setDescription(event.target.value)} disabled={!canWriteEco} />
                  </label>
                  {description.trim() ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Preview
                      </div>
                      <EngineeringChangeMarkdown>{description}</EngineeringChangeMarkdown>
                    </div>
                  ) : null}
                  <label className="block text-sm font-medium text-slate-700 md:col-span-2">
                    Implementation plan (Markdown, SGS only — not on ECNs)
                    <textarea
                      className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 font-mono text-sm"
                      rows={8}
                      value={implementationPlan}
                      onChange={(event) => setImplementationPlan(event.target.value)}
                      disabled={!canWriteEco}
                    />
                  </label>
                  {implementationPlan.trim() ? (
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4 md:col-span-2">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Implementation plan preview
                      </div>
                      <EngineeringChangeMarkdown>{implementationPlan}</EngineeringChangeMarkdown>
                    </div>
                  ) : null}
                  <label className="block text-sm font-medium text-slate-700 md:col-span-2">
                    Change reason
                    <textarea className="mt-1 w-full rounded-md border border-slate-200 px-3 py-2 text-sm" rows={2} value={changeReason} onChange={(event) => setChangeReason(event.target.value)} disabled={!canWriteEco} />
                  </label>
                </div>
              ) : (
                <>
                  {eco.description ? <EngineeringChangeMarkdown>{eco.description}</EngineeringChangeMarkdown> : null}
                  {eco.implementation_plan ? (
                    <div className="mt-4 rounded-md border border-slate-200 bg-slate-50 p-4">
                      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Implementation plan (SGS only)
                      </p>
                      <EngineeringChangeMarkdown>{eco.implementation_plan}</EngineeringChangeMarkdown>
                    </div>
                  ) : null}
                  {eco.change_reason ? <p className="text-sm text-slate-600">{eco.change_reason}</p> : null}
                  {eco.enforcement_rule ? <p className="text-sm text-slate-600">Effective condition: {eco.enforcement_rule}</p> : null}
                </>
              )}
              <div className="flex flex-wrap gap-2">
                {eco.status === "draft" ? (
                  <ReadOnlyHoverLayer active={!canWriteEco} hoverHint={readOnlyEcoHint}>
                    <Button type="button" size="sm" variant="secondary" disabled={!canWriteEco} onClick={() => ecoActionMutation.mutate("submit")}>Submit</Button>
                  </ReadOnlyHoverLayer>
                ) : null}
                {eco.status === "submitted" ? (
                  <ReadOnlyHoverLayer active={!canWriteEco} hoverHint={readOnlyEcoHint}>
                    <Button type="button" size="sm" variant="secondary" disabled={!canWriteEco} onClick={() => ecoActionMutation.mutate("approve")}>Approve</Button>
                  </ReadOnlyHoverLayer>
                ) : null}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>Applicability</CardTitle></CardHeader>
            <CardBody className="space-y-3">
              {eco.status === "draft" ? (
                <div className="space-y-2">
                  <p className="text-sm text-slate-600">Product collections</p>
                  <div className="flex flex-wrap gap-2">
                    {(collectionsQuery.data?.collections ?? []).map((collection) => {
                      const selected = collectionIds.includes(collection.id);
                      return (
                        <Button
                          key={collection.id}
                          type="button"
                          size="sm"
                          variant={selected ? "primary" : "secondary"}
                          disabled={!canWriteEco}
                          onClick={() => {
                            setCollectionIds((previous) =>
                              selected
                                ? previous.filter((value) => value !== collection.id)
                                : [...previous, collection.id],
                            );
                          }}
                        >
                          {collection.code}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                detail.productCollections.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {detail.productCollections.map((row) => (
                      <Link
                        key={row.product_collection_id}
                        className="text-sm font-medium text-brand-700 hover:underline"
                        to={`/documents/collections/${row.product_collection_id}`}
                      >
                        {row.code.toUpperCase()} family
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-slate-800">No product families linked.</p>
                )
              )}
              {detail.skus.length > 0 ? (
                <p className="text-sm text-slate-600">Part numbers: {detail.skus.map((row) => row.sku).join(", ")}</p>
              ) : null}
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>ECNs (contract manufacturers)</CardTitle></CardHeader>
            <CardBody className="space-y-4">
              <p className="text-sm text-slate-600">
                Record when you communicated each notice outside the hub. The hub does not send email.
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {detail.changeNotices.map((notice) => (
                  <div key={notice.id} className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-slate-900">
                      {notice.supplier_name ?? "Supplier"} · {formatEcnNumber(notice.number)}
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${noticeStatusClass(notice.status)}`}>{notice.status}</span>
                  </div>
                  <div className="text-xs text-slate-600 space-y-1">
                    <div>CM reference: {notice.external_reference ?? "Pending"}</div>
                    <div>Issued: {notice.issued_at ? formatTimestamp(notice.issued_at) : "Not recorded"}</div>
                    <div>Effective: {notice.effective_date ?? "Not set"}</div>
                    <div>Acknowledged: {notice.acknowledged_at ? formatTimestamp(notice.acknowledged_at) : "Pending"}</div>
                    <div>CM compliant: {notice.implemented_at ? formatTimestamp(notice.implemented_at) : "Pending"}</div>
                    {notice.cm_response_original_filename ? (
                      <div>
                        CM response:{" "}
                        {notice.cm_response_download_url ? (
                          <a
                            className="font-medium text-brand-700 hover:underline"
                            href={notice.cm_response_download_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            {notice.cm_response_original_filename}
                          </a>
                        ) : (
                          notice.cm_response_original_filename
                        )}
                        {notice.cm_response_uploaded_at
                          ? ` (${formatTimestamp(notice.cm_response_uploaded_at)})`
                          : null}
                      </div>
                    ) : null}
                    <div>SGS verification: {notice.implementation_checked_at ? formatTimestamp(notice.implementation_checked_at) : "Pending"} ({notice.verification_result ?? "pending"})</div>
                    {notice.notes ? <div>Communication notes: {notice.notes}</div> : null}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {notice.status === "draft" ? (
                      <>
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            window.open(changeNoticeHtmlPreviewUrl(id!, notice.id), "_blank", "noopener,noreferrer")
                          }
                        >
                          Preview HTML
                        </Button>
                        <ReadOnlyHoverLayer active={!canWriteEcn} hoverHint={readOnlyEcnHint}>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={!canWriteEcn || dirty || saveMutation.isPending || ecnPdfMutation.isPending}
                            title={dirty || saveMutation.isPending ? "Save the change order before you download." : undefined}
                            onClick={() => ecnPdfMutation.mutate(notice.id)}
                          >
                            Download PDF
                          </Button>
                        </ReadOnlyHoverLayer>
                        <input
                          ref={(element) => {
                            cmResponseInputRefs.current[notice.id] = element;
                          }}
                          type="file"
                          accept="application/pdf,.pdf"
                          className="hidden"
                          onChange={(event) => {
                            const file = event.target.files?.[0];
                            event.target.value = "";
                            if (!file || !canWriteEcn) return;
                            ecnCmResponseMutation.mutate({ noticeId: notice.id, file });
                          }}
                        />
                        <ReadOnlyHoverLayer active={!canWriteEcn} hoverHint={readOnlyEcnHint}>
                          <Button
                            type="button"
                            size="sm"
                            variant="primary"
                            disabled={!canWriteEcn || ecnCmResponseMutation.isPending}
                            onClick={() => cmResponseInputRefs.current[notice.id]?.click()}
                          >
                            Upload response and record as implemented
                          </Button>
                        </ReadOnlyHoverLayer>
                      </>
                    ) : null}
                    {notice.status === "issued" ? (
                      <ReadOnlyHoverLayer active={!canWriteEcn} hoverHint={readOnlyEcnHint}>
                        <Button type="button" size="sm" variant="secondary" disabled={!canWriteEcn} onClick={() => noticeActionMutation.mutate({ noticeId: notice.id, action: "acknowledge" })}>
                          Record acknowledgement
                        </Button>
                      </ReadOnlyHoverLayer>
                    ) : null}
                    {notice.status === "acknowledged" ? (
                      <ReadOnlyHoverLayer active={!canWriteEcn} hoverHint={readOnlyEcnHint}>
                        <Button type="button" size="sm" variant="secondary" disabled={!canWriteEcn} onClick={() => noticeActionMutation.mutate({ noticeId: notice.id, action: "implement" })}>
                          Record CM implementation
                        </Button>
                      </ReadOnlyHoverLayer>
                    ) : null}
                    {notice.status === "implemented" ? (
                      <>
                        <ReadOnlyHoverLayer active={!canWriteEcn} hoverHint={readOnlyEcnHint}>
                          <Button type="button" size="sm" variant="secondary" disabled={!canWriteEcn} onClick={() => patchNoticeMutation.mutate({ noticeId: notice.id, verificationResult: "confirmed" })}>
                            Mark SGS verification confirmed
                          </Button>
                        </ReadOnlyHoverLayer>
                        <ReadOnlyHoverLayer active={!canWriteEcn} hoverHint={readOnlyEcnHint}>
                          <Button type="button" size="sm" variant="primary" disabled={!canWriteEcn || notice.verification_result !== "confirmed"} onClick={() => noticeActionMutation.mutate({ noticeId: notice.id, action: "verify" })}>
                            Verify ECN
                          </Button>
                        </ReadOnlyHoverLayer>
                      </>
                    ) : null}
                  </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap items-end gap-2 border-t border-slate-100 pt-3">
                <div className="min-w-[16rem] flex-1">
                  <label className="block text-sm font-medium text-slate-700">Add ECN recipient</label>
                  <SupplierSearchableSelect
                    className="mt-1"
                    value={newNoticeSupplierId != null ? String(newNoticeSupplierId) : ""}
                    searchValue={newNoticeSupplierSearch}
                    onSearchChange={setNewNoticeSupplierSearch}
                    onValueChange={(option) =>
                      setNewNoticeSupplierId(option?.value ? Number.parseInt(option.value, 10) : null)
                    }
                    disabled={!canWriteEco}
                  />
                </div>
                <ReadOnlyHoverLayer active={!canWriteEco} hoverHint={readOnlyEcoHint}>
                  <Button type="button" size="sm" variant="primary" disabled={!canWriteEco || newNoticeSupplierId == null} onClick={() => createNoticeMutation.mutate()}>
                    Add ECN
                  </Button>
                </ReadOnlyHoverLayer>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader><CardTitle>Audit history</CardTitle></CardHeader>
            <CardBody>
              {detail.events.length === 0 ? (
                <p className="text-sm text-slate-500">No events yet.</p>
              ) : (
                <ul className="space-y-2 text-sm text-slate-700">
                  {detail.events.map((event) => (
                    <li key={event.id}>
                      <span className="font-medium">{event.event_type}</span>
                      {event.created_by_display ? ` · ${event.created_by_display}` : null}
                      <span className="text-slate-500"> · {formatTimestamp(event.created_at)}</span>
                      {event.event_note ? <div className="text-slate-600">{event.event_note}</div> : null}
                    </li>
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
}
